import React, { useState } from 'react';
import { useQuery, useMutation } from "@apollo/client";
import get from 'lodash.get';
import { prop } from 'styled-tools';
import styled from 'styled-components';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import ModalWithTrigger from 'components/ModalWithTrigger';
import Accordion from 'components/Accordion';
import CancelAccount from './CancelAccount';
import EmailPreferences from './EmailPreferences';
import { useAuth } from 'context/AuthContext';
import YourInfo from './YourInfo';
import { dataLayerPush } from 'utils/dataLayer';
import { Redirect } from 'react-router-dom';
import getEmailPreferencesQuery from './getEmailPreferences.graphql';
import createBasicContact from './createBasicContact.graphql';

const AccountContainer = styled(Container)`
  && {
    ${props => props.theme.breakpoints.up('lg')} {
      max-width: 1920px;
      padding-left: 8.75rem;
      padding-right: 8.75rem;
    }
  }
`;

const AccountHeader = styled(Typography)`
  margin-bottom: 4.125rem;
  margin-top: 2.125rem;
`;

const AccordionContainer = styled.div`
  .MuiAccordion-root {
    border: 0.0625rem solid ${prop('theme.colors.borderGray')};
    margin-bottom: 4.25rem;
    min-height: 5rem;

    h4 {
      font-size: 1.25rem;
    }

    &.Mui-expanded {
      margin-bottom: 4.25rem;

      &:last-child {
        margin-bottom: 4.25rem;
      }
    }
  }

  .MuiAccordionSummary-root {
    height: 5rem;

    &.Mui-expanded {
      border-bottom: 0.0625rem solid ${prop('theme.colors.borderGray')};
    }
  }

  .MuiAccordionDetails-root {
    padding: 0;
  }
`;

const CancelContainer = styled.div`
  margin-bottom: 3.75rem;

  button {
    font-family: ${prop('theme.typography.fontFamilySecondary')};
    font-size: 0.8125rem;
  }
`;

function Account() {
  const { user } = useAuth();
  const auth0Id = get(user, 'sub', '');
  const [open, setOpen] = useState(false);
  const [preferenceLoading, setPreferenceLoading] = useState(false);
  const [resubscribeError, setResubscribeError] = useState(null);
  const { data, loading: queryLoading, error, refetch } = useQuery(getEmailPreferencesQuery, {
    skip: typeof window === 'undefined',
  });
  const [createContact, { loading: contactLoading }] = useMutation(createBasicContact);
  const emailPreferenceGroups = data?.communicationPreferences?.emailPreferences?.preferenceGroups ?? [];

  const handleOpen = () => {
    dataLayerPush(`Click to open 'Cancel Your tastylive Account'`);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleResubscribe = async () => {
    try {
      const result = await createContact({
        variables: {
          input: {
            email: user.email,
            firstName: user.given_name,
            lastName: user.family_name,
            leadSource: 'tastytrade'
          }
        }
      });

      if (result.data.createBasicContact.success) {
        setPreferenceLoading(true); 
        retryFetchEmailPreferences();
      }
    } catch (err) {
      console.error('Error re-subscribing contact:', err);
      setResubscribeError('An error occurred while re-subscribing. Please refresh to try again.');
    }
  };

  const retryFetchEmailPreferences = () => {
    const retryDelay = 500; 
  
    const fetchPreferences = async () => {
      try {
        const preferencesResult = await refetch();
        if (preferencesResult?.data?.communicationPreferences) {
          setPreferenceLoading(false);
        } else {
          setTimeout(fetchPreferences, retryDelay);
        }
      } catch (error) {
        console.error('Error fetching email preferences, retrying:', error);
        setTimeout(fetchPreferences, retryDelay);
      }
    };
  
    fetchPreferences(); 
  };

  if (!user) {
    return <Redirect to='/' />
  }

  const loading = queryLoading || contactLoading || preferenceLoading;
  const baseEmailPreferences = emailPreferenceGroups.filter(grp => grp.groupHeader !== 'Learn Center');

  return (
    <Box py={4}>
      <AccountContainer>
        <AccountHeader variant='h1'>Manage Your Profile</AccountHeader>
        <AccordionContainer>
          <Accordion
            icon='chevronDown'
            id='panel1'
            title='Your Info'
            variant='h4'
          >
            <YourInfo auth0Id={auth0Id} />
          </Accordion>
          <Accordion
            icon='chevronDown'
            id='panel2'
            title='Email Preferences'
            variant='h4'
          >
            <EmailPreferences
              preferences={baseEmailPreferences}
              loading={loading}
              error={error}
              onResubscribe={handleResubscribe}
              resubscribeError={resubscribeError}
            />
          </Accordion>
        </AccordionContainer>
        <CancelContainer>
          <ModalWithTrigger
            ariaLabel='Cancel tastylive account'
            ariaDescribe='Interaction to cancel your tastylive account'
            handleClose={handleClose}
            handleOpen={handleOpen}
            height={658}
            open={open}
            padding='wide'
            triggerText='Cancel Your Tastylive Account'
            triggerType='link'
            width={924}
          >
            <CancelAccount onClose={handleClose} />
          </ModalWithTrigger>
        </CancelContainer>
      </AccountContainer>
    </Box>
  );
}

export default React.memo(Account);
